import { useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { authClient } from "../auth/client.js";

// 1.D.3 Login-naekyma. Email + password → Better-Auth signIn.email().
// Onnistuessa redirect "/", virheessa nayttaa serverin viestin.

export type LoginValidation = {
  email: string;
  password: string;
  error: string | null;
};

/** Puhdas validointi — yksikkotestattava ilman DOM:ia. */
export function validateLoginInput(email: string, password: string): LoginValidation {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    return { email: trimmedEmail, password, error: "Sahkopostiosoite vaaditaan" };
  }
  if (!trimmedEmail.includes("@")) {
    return { email: trimmedEmail, password, error: "Sahkopostiosoite ei kelpaa" };
  }
  if (password.length < 8) {
    return { email: trimmedEmail, password, error: "Salasana liian lyhyt (min 8 merkkia)" };
  }
  return { email: trimmedEmail, password, error: null };
}

export function Login() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const v = validateLoginInput(email, password);
    if (v.error) {
      setError(v.error);
      return;
    }
    setSubmitting(true);
    const result = await authClient.signIn.email({
      email: v.email,
      password: v.password,
    });
    setSubmitting(false);
    if (result.error) {
      setError(result.error.message ?? "Kirjautuminen epaonnistui");
      return;
    }
    navigate("/");
  }

  return (
    <section className="mx-auto max-w-sm px-6 py-16">
      <h2 className="text-2xl font-semibold tracking-tight">Kirjaudu sisaan</h2>
      <form className="mt-6 flex flex-col gap-3" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-300">Sahkoposti</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 focus:border-slate-400 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-300">Salasana</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 focus:border-slate-400 focus:outline-none"
          />
        </label>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-white disabled:opacity-60"
        >
          {submitting ? "Kirjaudutaan..." : "Kirjaudu"}
        </button>
      </form>
    </section>
  );
}
